/** Which tab a returning staffer lands on.
 *
 *  A REMEMBERED TAB THAT NO LONGER EXISTS IS NOT A REASON TO SHOW SOMEBODY HOME. `Addresses` and
 *  `Placements` were top-level tabs until the vault surfaces were gathered under one; anyone whose
 *  last visit ended on either has that string in localStorage. Dropping them on Home would be a
 *  working queue vanishing with no explanation -- so a moved tab RESOLVES to where it moved,
 *  including the sub-tab, and only a genuinely unknown value falls back to Home.
 *
 *  A RETIRED tab is the same problem with a different answer: there is nowhere it moved to, so it
 *  resolves to the nearest surface that still exists rather than to its own dead name.
 *
 *  Lives in its own module because it is the one piece of routing with a right and a wrong answer,
 *  and a useState initialiser is not a thing a test can reach.
 */
export type Tab = "home" | "accounts" | "team" | "import" | "voted" | "elections" | "audit" | "vault" | "health" | "versions" | "devices" | "money" | "texting";

export const TABS: Tab[] = ["home", "accounts", "team", "import", "voted", "elections", "audit", "vault", "health", "versions", "devices", "money", "texting"];

/** Where each retired tab went. Kept as data rather than a switch so adding the next move is one
 *  line, and so the test can assert every entry lands somewhere that still exists. */
export const MOVED: Record<string, { tab: Tab; sub: string }> = {
  addresses:  { tab: "vault", sub: "addresses" },
  // RETIRED, NOT MOVED (2026-09-11), and that is why it does not point at its own name. The
  // Placement review screen is gone -- its backlog is cleared and 0245 retired the pass that
  // filled it -- so anyone still carrying "placements" in localStorage lands on the vault
  // overview. Pointing a MOVED entry at a sub-tab that no longer renders is a blank screen, not a
  // redirect; the test below now asserts every entry here names a sub-tab that exists.
  placements: { tab: "vault", sub: "overview" },
  // 2026-09-02. Geocodes was the last address-data tab in the top nav. The company portal is the
  // business back end -- accounts, staff, billing, provisioning -- and address data is ONE subject
  // inside it, not two of its eight tabs. The Vault/Geocodes SPLIT was right and is kept as two
  // sub-tabs; what was wrong was giving it two slots in the company's own navigation.
  geocode:    { tab: "vault", sub: "voterfile" },
};

/** The vault sub-tabs that exist, and where an unknown one lands.
 *
 *  THE SECOND DOOR (2026-09-11). resolveTab guards the TOP-LEVEL tab, and 0748 made it guard the
 *  sub-tab a MOVED entry points at. Neither covers the case that actually bites: App.tsx restores
 *  `portal.vault` straight out of localStorage with no check at all, so anyone whose last visit
 *  ended on a sub-tab that has since been retired gets that exact string handed back to Vault.tsx.
 *
 *  Vault.tsx falls back to the overview for the HEADING but renders its screens with `sub === "..."`
 *  comparisons, so an unknown value draws a header with nothing under it. Two sub-tabs have been
 *  retired in two days -- Placement review and Where to send -- and anybody who was last on either
 *  has it stored right now. It presents as the portal being broken, with no way to get back except
 *  clicking another rail item.
 *
 *  Kept here rather than in Vault.tsx for the reason this module exists: routing with a right and a
 *  wrong answer belongs somewhere a test can reach, and a useState initialiser is not.
 */
export const VAULT_SUB_IDS = ["overview", "coverage", "addresses", "voterfile"] as const;

export function resolveVaultSub(saved: string | null): string {
  return saved && (VAULT_SUB_IDS as readonly string[]).includes(saved) ? saved : "overview";
}

export function resolveTab(saved: string | null): { tab: Tab; vaultSub?: string } {
  if (saved && MOVED[saved]) return { tab: MOVED[saved].tab, vaultSub: MOVED[saved].sub };
  if (saved && (TABS as string[]).includes(saved)) return { tab: saved as Tab };
  return { tab: "home" };
}
